import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import type { Channel } from '../../types';

interface ChannelCardProps {
    channel: Channel;
    index?: number;
    className?: string;
}

const ChannelCard: React.FC<ChannelCardProps> = ({
    channel,
    index = 0,
    className = ''
}) => {
    const navigate = useNavigate();
    const [isHovered, setIsHovered] = useState(false);

    const handleClick = () => {
        navigate(`/channel/${channel.id}`);
    };

    return (
        <div
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={`
        group relative cursor-pointer
        animate-fade-in
        ${className}
      `}
            style={{ animationDelay: `${index * 60}ms` }}
        >
            {/* Card Container */}
            <div className={`
        relative aspect-video rounded-2xl sm:rounded-3xl overflow-hidden
        bg-gradient-to-br ${channel.color || 'from-gray-700 to-gray-900'}
        shadow-lg shadow-black/30
        transform transition-all duration-500 ease-out
        group-hover:scale-[1.03] group-hover:shadow-2xl
        group-focus-within:scale-[1.03]
        ring-2 ring-white/10 group-hover:ring-white/30
      `}>

                {/* Animated Background Pattern */}
                <div className="absolute inset-0 opacity-20">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
                             radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)`,
                    }} />
                </div>

                {/* Live Indicator */}
                <div className="absolute top-3 left-3 sm:top-4 sm:left-4
          flex items-center gap-2 px-3 py-1.5 rounded-full
          bg-black/40 backdrop-blur-sm">
                    <span className="relative flex h-2 w-2 sm:h-2.5 sm:w-2.5">
                        <span className={`animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75
              ${isHovered ? '' : 'animation-paused'}`}></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 sm:h-2.5 sm:w-2.5 bg-red-500"></span>
                    </span>
                    <span className="text-white text-xs font-semibold">LIVE</span>
                </div>

                {/* Category Badge */}
                <div className="absolute top-3 right-3 sm:top-4 sm:right-4
          px-3 py-1.5 rounded-full
          bg-white/10 backdrop-blur-sm">
                    <span className="text-white/90 text-xs font-medium">{channel.category}</span>
                </div>

                {/* Channel Logo/Icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className={`
            transform transition-all duration-500
            ${isHovered ? 'scale-110 rotate-3' : 'scale-100 rotate-0'}
          `}>
                        <channel.logo className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 text-white/90 drop-shadow-lg" />
                    </div>
                </div>

                {/* Channel Name */}
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 lg:p-6
          bg-gradient-to-t from-black/80 to-transparent">
                    <h3 className="text-white font-bold text-lg sm:text-xl lg:text-2xl xl:text-3xl
            drop-shadow-lg transform transition-transform duration-300
            group-hover:translate-x-2">
                        {channel.name}
                    </h3>

                    {/* Watch Now indicator */}
                    <div className="flex items-center gap-2 mt-2 text-white/80
            transform translate-y-4 opacity-0 
            group-hover:translate-y-0 group-hover:opacity-100
            transition-all duration-300">
                        <FontAwesomeIcon icon={faPlay} className="text-sm" />
                        <span className="text-sm font-medium">Watch Now</span>
                    </div>
                </div>

                {/* Play overlay on hover */}
                <div className="absolute inset-0 flex items-center justify-center
          bg-black/30 opacity-0 group-hover:opacity-100
          transition-opacity duration-300">
                    <div className="p-5 sm:p-6 rounded-full bg-white/20 backdrop-blur-sm
            transform scale-75 group-hover:scale-100
            transition-transform duration-300">
                        <FontAwesomeIcon icon={faPlay} className="text-white text-2xl sm:text-3xl ml-1" />
                    </div>
                </div>

                {/* Signal strength indicator */}
                <div className="absolute bottom-4 right-4 
          flex items-center gap-1
          opacity-0 group-hover:opacity-100
          transition-opacity duration-300">
                    {[1, 2, 3, 4].map((bar) => (
                        <div
                            key={bar}
                            className={`w-1 bg-green-400 rounded-full transition-all duration-300`}
                            style={{
                                height: `${bar * 4}px`,
                                animationDelay: `${bar * 100}ms`
                            }}
                        />
                    ))}
                </div>
            </div>

            {/* Focus ring for accessibility */}
            <button
                className="absolute inset-0 rounded-2xl sm:rounded-3xl 
          focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-500/50"
                aria-label={`Watch ${channel.name}`}
            />
        </div>
    );
};

export default ChannelCard;
